import { FC, useState, useEffect } from "react";
import { useApp } from "../context/useApp";
import { cache, ROUTES, rumba, metrics } from "../utils/symbols";
import { MonthsAvailability } from "../types";
import { DateSelector } from "../parts/date-selector/DateSelector";
import { DEFAULT_DIALOG_HANDLE } from "../utils/symbols";

interface DateSelectionProps {
    asCalendarWidget?: boolean
    at?: string | null
    from?: string | null
    displayedMonthsQty?: number | null
    value?: string | null
    specialDates?: string[]
    showLegend?: boolean
}

export const DateSelection: FC<DateSelectionProps> = ({
    asCalendarWidget, 
    at, 
    from, 
    displayedMonthsQty, 
    value,
    specialDates,
    showLegend,
}) => {
    // todo: move availability to provider to not loose the cache when changing step
    const [availability, setAvailability] = useState<MonthsAvailability>({})
    // MonthsAvailability keys -> yyyy-m with january as 1

    const {
        state: { booking, dialogHandles },
        actions: { navigate, genericError }
    } = useApp()

    const calendarDialog = dialogHandles.get('calendar-dialog') ?? DEFAULT_DIALOG_HANDLE

    const handleDateSelection = async (date: string): Promise<void> => {
        if (booking.date.split('T')[0] !== date) {
            booking.selectDate(date)
            await metrics.dateSelectionEvent(booking.getBookingDataByReference())
        }
        if (asCalendarWidget) {
            window.parent.location.href = (window as any).bookingEnginePage + `?date=${date}`
            return
        }
        booking.setBookingStep('shift').render()
        navigate(ROUTES.TABLE_LAYOUT)
    }

    const getMonthAvailability = async (date: Date): Promise<void> => {
        const month = `${date.getFullYear()}-${date.getMonth() + 1}`
        const availabilityInCache = cache.get('availability',month)
        if (availabilityInCache) {
            setAvailability(months => ({...months, [month]: availabilityInCache}))
            return
        }
        calendarDialog.openDialog({ dialog: 'loading', className: 'w-100 df aic jcc loading' })
        const response = await rumba.getMonthAvailability(date)
        calendarDialog.closeDialog()
        if (response.result !== 'ok') {
            genericError(() => getMonthAvailability(date))
            return
        }
        const bookingDate = booking.date.substring(0, 10)
        if (booking.date !== '' && response.data[bookingDate] && !response.data[bookingDate].available) {
            booking.unSelectDate().render()
        }
        setAvailability(months => ({...months, [month]: response.data}))
        cache.add('availability', month, response.data)
    }

    useEffect(() => {

        if (!dialogHandles.has('calendar-dialog')) return
        /* request first two months. (Two months may be visible) 
         * if user go back to date step, re-request availability due to
         * availability changes during booking process */
        const dateToRequest = new Date(booking.date || from || Date.now())
        getMonthAvailability(dateToRequest)
        getMonthAvailability(new Date(dateToRequest.getFullYear(), dateToRequest.getMonth() + 1, 1))
        //eslint-disable-next-line
    }, [dialogHandles])

    return (
        <DateSelector
            displayedMonthsQty={displayedMonthsQty ?? 12}
            from={from ?? Date.now()}
            at={at ?? booking.date}
            value={value ?? booking.date}
            monthsAvailability={availability}
            onDateSelected={handleDateSelection}
            onMonthChange={getMonthAvailability}
            specialDates={specialDates}
            showLegend={showLegend}
        />
    )
}